/**
 * UserManagement — TASK-022 / flow-04, flow-21
 * Actor: sys_admin, biz_admin
 * Route: /admin/users
 * API: GET/POST/PUT /api/v1/users
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  AppBadge, AppButton, AppCard, AppField, AppInput, AppModal, AppSelect, ConfirmAction,
} from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";
import { getUser, hasPermission } from "../../auth/session";
import { useToast } from "../../../shared/toast/ToastProvider";

type User = {
  id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  phone?: string | null;
  role: string;
  is_active: boolean;
  tenant_id: number | null;
  account_scope?: "platform" | "company";
  account_scope_label?: string;
  pending_approval?: boolean;
  mfa_enabled?: boolean;
  created_at: string;
};

type PendingUser = {
  id: number;
  username: string;
  email: string;
  tenant_id: number | null;
  requested_at: string | null;
  first_name?: string | null;
  last_name?: string | null;
  auth_provider?: string | null;
};

const ROLE_LABELS: Record<string, string> = {
  system_admin: "System Admin",
  business_admin: "Business Admin",
  commercial_officer: "Commercial Officer",
  technician: "Technician",
  sales_rep: "Sales Rep",
  village_chairman: "Village Chairman",
  m_and_e_officer: "M&E Officer",
  rea_officer: "REA Officer",
  customer: "Customer",
};

const FIELD_CREATABLE_ROLES = ["sales_rep", "technician", "village_chairman", "customer"] as const;
/** Platform bootstrap: only platform ops + first company BA. */
const PLATFORM_CREATABLE_ROLES = ["system_admin", "business_admin"] as const;

function CreateUserModal({
  open, onClose, onSuccess, canManage,
}: {
  open: boolean; onClose: () => void; onSuccess: () => void; canManage: boolean;
}) {
  const toast = useToast();
  const caller = getUser();
  const isPlatform = caller?.role === "system_admin" || caller?.account_scope === "platform";
  const roleOptions = Object.entries(ROLE_LABELS).filter(([value]) => {
    if (isPlatform && canManage) {
      return PLATFORM_CREATABLE_ROLES.includes(value as (typeof PLATFORM_CREATABLE_ROLES)[number]);
    }
    if (canManage) {
      return value !== "system_admin";
    }
    return FIELD_CREATABLE_ROLES.includes(value as (typeof FIELD_CREATABLE_ROLES)[number]);
  });
  const [form, setForm] = useState({
    username: "", first_name: "", last_name: "", email: "",
    phone: "", role: isPlatform && canManage ? "business_admin" : "sales_rep", password: "", tenant_id: "",
  });
  const [tenants, setTenants] = useState<{ id: number; name: string; code: string }[]>([]);
  const [groups, setGroups] = useState<{ id: number; name: string; code: string }[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const needsCompany = form.role !== "system_admin";

  useEffect(() => {
    if (!open) return;
    if (canManage) {
      api.get<{ security_groups: { id: number; name: string; code: string; is_active: boolean }[] }>(
        isPlatform && form.tenant_id
          ? `/security-groups?tenant_id=${encodeURIComponent(form.tenant_id)}&is_active=true`
          : "/security-groups?is_active=true",
      )
        .then((d) => setGroups((d.security_groups ?? []).filter((g) => g.is_active)))
        .catch(() => setGroups([]));
    }
    if (canManage && isPlatform) {
      api.get<{ tenants: { id: number; name: string; code: string }[] }>("/tenants")
        .then((d) => setTenants(d.tenants ?? []))
        .catch(() => setTenants([]));
    }
  }, [open, isPlatform, form.tenant_id, canManage]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.username || !form.email || !form.password) {
      setErr("Username, email and password are required");
      return;
    }
    if (isPlatform && needsCompany && !form.tenant_id) {
      setErr("Company users require a company (tenant)");
      return;
    }
    setBusy(true); setErr("");
    try {
      await api.post("/users", {
        username: form.username,
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        password: form.password,
        ...(isPlatform && needsCompany && form.tenant_id
          ? { tenant_id: Number(form.tenant_id) }
          : {}),
        ...(canManage && groupIds.length ? { security_group_ids: groupIds } : {}),
      });
      toast.success(`User ${form.username} created`);
      onSuccess(); onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to create user");
    }
    finally { setBusy(false); }
  };

  return (
    <AppModal open={open} onClose={onClose} title="Create User">
      <form onSubmit={submit}>
        <div className="pg-two-col-form">
          <AppField label="First Name">
            <AppInput id="user-fname" type="text" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} disabled={busy} autoFocus />
          </AppField>
          <AppField label="Last Name">
            <AppInput id="user-lname" type="text" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Username">
          <AppInput id="user-username" type="text" value={form.username} onChange={(e) => set("username", e.target.value)} disabled={busy} />
        </AppField>
        <AppField label="Email">
          <AppInput id="user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} disabled={busy} required />
        </AppField>
        <AppField label="Phone">
          <AppInput id="user-phone" type="tel" placeholder="+255..." value={form.phone} onChange={(e) => set("phone", e.target.value)} disabled={busy} />
        </AppField>
        <AppField label="Role">
          <AppSelect id="user-role" value={form.role} onChange={(e) => set("role", e.target.value)} disabled={busy}>
            {roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </AppSelect>
        </AppField>
        {canManage && isPlatform && needsCompany && (
          <p className="pg-muted" style={{ fontSize: 13, margin: "0 0 12px" }}>
            Platform may bootstrap only the <strong>first Business Admin</strong> for a company.
            That BA creates all other company staff and privileges.
          </p>
        )}
        {canManage && isPlatform && needsCompany && (
          <AppField label="Company (tenant)">
            <AppSelect
              id="user-tenant"
              value={form.tenant_id}
              onChange={(e) => {
                set("tenant_id", e.target.value);
                setGroupIds([]);
              }}
              disabled={busy}
              required
            >
              <option value="">Select company…</option>
              {tenants.map((t) => (
                <option key={t.id} value={String(t.id)}>{t.name} ({t.code})</option>
              ))}
            </AppSelect>
          </AppField>
        )}
        {canManage && isPlatform && !needsCompany && (
          <p className="pg-muted" style={{ fontSize: 13, margin: "0 0 12px" }}>
            Platform user — no company assignment (sees all tenants).
          </p>
        )}
        {canManage ? <AppField label="Security groups (optional)">
          <div style={{ maxHeight: 140, overflow: "auto", display: "grid", gap: 4 }}>
            {groups.length === 0 && <span className="pg-muted">No groups yet — create them under Security Groups.</span>}
            {groups.map((g) => (
              <label key={g.id} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={groupIds.includes(g.id)}
                  onChange={() =>
                    setGroupIds((ids) =>
                      ids.includes(g.id) ? ids.filter((x) => x !== g.id) : [...ids, g.id],
                    )
                  }
                  disabled={busy}
                />
                {g.name} <span className="pg-muted">({g.code})</span>
              </label>
            ))}
          </div>
        </AppField> : null}
        <AppField label="Password" error={err}>
          <AppInput id="user-password" type="password" placeholder="Min 8 characters" value={form.password} onChange={(e) => set("password", e.target.value)} disabled={busy} />
        </AppField>
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14}/> Creating…</> : "Create User"}</AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
        </div>
      </form>
    </AppModal>
  );
}

function AssignGroupsModal({
  user, open, onClose, onSuccess,
}: {
  user: User; open: boolean; onClose: () => void; onSuccess: () => void;
}) {
  const [groups, setGroups] = useState<{ id: number; name: string; code: string }[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const sgQs = user.tenant_id != null
          ? `/security-groups?tenant_id=${encodeURIComponent(String(user.tenant_id))}&is_active=true`
          : "/security-groups?is_active=true";
        const [all, mine] = await Promise.all([
          api.get<{ security_groups: { id: number; name: string; code: string; is_active: boolean }[] }>(sgQs),
          api.get<{ security_groups: { id: number }[] }>(`/users/${user.id}/security-groups`),
        ]);
        setGroups((all.security_groups ?? []).filter((g) => g.is_active));
        setGroupIds((mine.security_groups ?? []).map((g) => g.id));
      } catch (ex: unknown) {
        setErr(ex instanceof Error ? ex.message : "Failed to load groups");
      }
    })();
  }, [open, user.id, user.tenant_id]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/users/${user.id}/security-groups`, { group_ids: groupIds });
      onSuccess(); onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to save");
    } finally { setBusy(false); }
  };

  return (
    <AppModal open={open} onClose={onClose} title={`Groups — ${user.username}`}>
      <p className="pg-muted">
        Groups refine API permissions <strong>within</strong> this user’s base role
        ({ROLE_LABELS[user.role] ?? user.role}). They cannot promote a Sales Rep to Business Admin —
        use <strong>Edit</strong> to change role. User must re-login after group changes.
      </p>
      {err && <p className="pg-error">{err}</p>}
      <div style={{ maxHeight: 240, overflow: "auto", display: "grid", gap: 4, margin: "12px 0" }}>
        {groups.map((g) => (
          <label key={g.id} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
            <input
              type="checkbox"
              checked={groupIds.includes(g.id)}
              onChange={() =>
                setGroupIds((ids) =>
                  ids.includes(g.id) ? ids.filter((x) => x !== g.id) : [...ids, g.id],
                )
              }
              disabled={busy}
            />
            {g.name} ({g.code})
          </label>
        ))}
      </div>
      <div className="pg-modal-actions">
        <AppButton type="button" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save groups"}</AppButton>
        <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
      </div>
    </AppModal>
  );
}

function EditUserModal({
  user, open, onClose, onSuccess,
}: {
  user: User; open: boolean; onClose: () => void; onSuccess: () => void;
}) {
  const caller = getUser();
  const isPlatform = caller?.role === "system_admin" || caller?.account_scope === "platform";
  const roleOptions = Object.entries(ROLE_LABELS).filter(([value]) => {
    if (isPlatform) {
      return PLATFORM_CREATABLE_ROLES.includes(value as (typeof PLATFORM_CREATABLE_ROLES)[number]);
    }
    return value !== "system_admin";
  });
  const [form, setForm] = useState({
    first_name: user.first_name ?? "",
    last_name: user.last_name ?? "",
    email: user.email,
    phone: user.phone ?? "",
    role: user.role,
    tenant_id: user.tenant_id != null ? String(user.tenant_id) : "",
  });
  const [tenants, setTenants] = useState<{ id: number; name: string; code: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");

  const needsCompany = form.role !== "system_admin";

  useEffect(() => {
    if (!open) return;
    setForm({
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      email: user.email,
      phone: user.phone ?? "",
      role: user.role,
      tenant_id: user.tenant_id != null ? String(user.tenant_id) : "",
    });
    setErr("");
    setNote("");
    if (isPlatform) {
      api.get<{ tenants: { id: number; name: string; code: string }[] }>("/tenants")
        .then((d) => setTenants(d.tenants ?? []))
        .catch(() => setTenants([]));
    }
  }, [open, user, isPlatform]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPlatform && needsCompany && !form.tenant_id) {
      setErr("Company users require a company (tenant)");
      return;
    }
    setBusy(true); setErr(""); setNote("");
    try {
      const body: Record<string, unknown> = {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email,
        phone: form.phone || null,
        role: form.role,
      };
      if (isPlatform && needsCompany) {
        body.tenant_id = Number(form.tenant_id);
      }
      const res = await api.put<{
        message: string;
        role_changed?: boolean;
        tenant_changed?: boolean;
        relogin_required?: boolean;
      }>(`/users/${user.id}`, body);
      if (res.relogin_required) {
        const bits = [
          res.role_changed ? "role" : null,
          res.tenant_changed ? "company" : null,
        ].filter(Boolean).join(" and ");
        setNote(
          `${bits ? bits.charAt(0).toUpperCase() + bits.slice(1) : "Access"} updated. `
          + "The user must sign out and sign in again so their JWT refreshes. "
          + (res.tenant_changed
            ? "Security groups from the previous company were revoked — re-assign under Groups if needed."
            : ""),
        );
      }
      onSuccess();
      if (!res.relogin_required) onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to update user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title={`Edit — ${user.username}`}>
      <form onSubmit={save}>
        <div className="pg-two-col-form">
          <AppField label="First Name">
            <AppInput value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} disabled={busy} />
          </AppField>
          <AppField label="Last Name">
            <AppInput value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} disabled={busy} />
          </AppField>
        </div>
        <AppField label="Email">
          <AppInput type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} disabled={busy} required />
        </AppField>
        <AppField label="Phone">
          <AppInput type="tel" placeholder="+255..." value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} disabled={busy} />
        </AppField>
        <AppField label="Role">
          <AppSelect
            value={form.role}
            onChange={(e) => {
              const next = e.target.value;
              setForm((f) => ({
                ...f,
                role: next,
                tenant_id: next === "system_admin" ? "" : f.tenant_id,
              }));
            }}
            disabled={busy}
          >
            {roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </AppSelect>
        </AppField>
        {isPlatform && needsCompany ? (
          <AppField label="Company (tenant)">
            <AppSelect
              value={form.tenant_id}
              onChange={(e) => setForm((f) => ({ ...f, tenant_id: e.target.value }))}
              disabled={busy}
              required
            >
              <option value="">Select company…</option>
              {tenants.map((t) => (
                <option key={t.id} value={String(t.id)}>{t.name} ({t.code})</option>
              ))}
            </AppSelect>
          </AppField>
        ) : null}
        {isPlatform && !needsCompany ? (
          <p className="pg-helper-text">Platform operator — no company assignment.</p>
        ) : null}
        {!isPlatform ? (
          <p className="pg-helper-text">
            Company is fixed to your tenant
            {caller?.tenant_id != null ? ` (#${caller.tenant_id})` : ""}.
          </p>
        ) : null}
        {err && <p className="pg-error">{err}</p>}
        {note && <p className="pg-muted">{note}</p>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>{busy ? "Saving…" : "Save changes"}</AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {note ? "Close" : "Cancel"}
          </AppButton>
        </div>
      </form>
    </AppModal>
  );
}

function ApproveUserModal({
  pendingUser,
  open,
  onClose,
  onSuccess,
}: {
  pendingUser: PendingUser;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const caller = getUser();
  const isPlatform = caller?.role === "system_admin" || caller?.account_scope === "platform";
  const roleOptions = Object.entries(ROLE_LABELS).filter(([value]) => {
    if (isPlatform) {
      return PLATFORM_CREATABLE_ROLES.includes(value as (typeof PLATFORM_CREATABLE_ROLES)[number]);
    }
    return value !== "system_admin";
  });

  const [tenants, setTenants] = useState<{ id: number; name: string; code: string }[]>([]);
  const [tenantId, setTenantId] = useState(
    pendingUser.tenant_id != null ? String(pendingUser.tenant_id) : "",
  );
  const [groups, setGroups] = useState<{ id: number; name: string; code: string; tenant_id?: number | null; tenant_name?: string | null }[]>([]);
  const [groupIds, setGroupIds] = useState<number[]>([]);
  const [role, setRole] = useState(isPlatform ? "business_admin" : "sales_rep");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const needsCompany = role !== "system_admin";
  const effectiveTenantId = isPlatform
    ? (needsCompany && tenantId ? Number(tenantId) : null)
    : (caller?.tenant_id ?? pendingUser.tenant_id ?? null);

  useEffect(() => {
    if (!open) return;
    setTenantId(pendingUser.tenant_id != null ? String(pendingUser.tenant_id) : "");
    setGroupIds([]);
    setErr("");
    if (isPlatform) {
      api.get<{ tenants: { id: number; name: string; code: string }[] }>("/tenants")
        .then((d) => setTenants(d.tenants ?? []))
        .catch(() => setTenants([]));
    }
  }, [open, pendingUser, isPlatform]);

  useEffect(() => {
    if (!open) return;
    // Platform must pick a company first so we don't show every tenant's duplicate role packs.
    if (isPlatform && needsCompany && !tenantId) {
      setGroups([]);
      return;
    }
    const q = isPlatform && tenantId
      ? `/security-groups?tenant_id=${encodeURIComponent(tenantId)}&is_active=true`
      : "/security-groups?is_active=true";
    api.get<{ security_groups: { id: number; name: string; code: string; tenant_id?: number | null; tenant_name?: string | null; is_active: boolean }[] }>(q)
      .then((d) => setGroups((d.security_groups ?? []).filter((g) => g.is_active)))
      .catch((ex: unknown) => setErr(ex instanceof Error ? ex.message : "Failed to load groups"));
  }, [open, isPlatform, needsCompany, tenantId]);

  const groupLabel = (g: { name: string; code: string; tenant_name?: string | null }) =>
    g.tenant_name ? `${g.name} · ${g.tenant_name}` : `${g.name} (${g.code})`;

  const approve = async () => {
    if (isPlatform && needsCompany && !tenantId) {
      setErr("Assign this user to a company before approving.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/users/${pendingUser.id}/approve`, {
        role,
        ...(isPlatform && needsCompany && tenantId
          ? { tenant_id: Number(tenantId) }
          : {}),
        security_group_ids: groupIds,
      });
      onSuccess();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to approve user");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/users/${pendingUser.id}/reject`, { reason: "Rejected by admin from approval queue" });
      onSuccess();
      onClose();
    } catch (ex: unknown) {
      setErr(ex instanceof Error ? ex.message : "Failed to reject user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title={`Approve account — ${pendingUser.username}`}>
      {err && <p className="pg-error">{err}</p>}
      <p className="pg-helper-text" style={{ marginBottom: 12 }}>
        {pendingUser.email}
        {effectiveTenantId == null && isPlatform
          ? " — pick a company so this person becomes a company user, not a platform operator."
          : null}
      </p>

      <AppField label="Role">
        <AppSelect
          value={role}
          onChange={(e) => {
            const next = e.target.value;
            setRole(next);
            if (next === "system_admin") setTenantId("");
          }}
          disabled={busy}
        >
          {roleOptions.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </AppSelect>
      </AppField>

      {isPlatform && needsCompany ? (
        <AppField label="Company (tenant)">
          <AppSelect
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              setGroupIds([]);
            }}
            disabled={busy}
            required
          >
            <option value="">Select company…</option>
            {tenants.map((t) => (
              <option key={t.id} value={String(t.id)}>{t.name} ({t.code})</option>
            ))}
          </AppSelect>
        </AppField>
      ) : null}

      {isPlatform && !needsCompany ? (
        <p className="pg-helper-text">Platform operator — no company assignment.</p>
      ) : null}

      {!isPlatform ? (
        <p className="pg-helper-text">
          Company user will join <strong>your company</strong>
          {caller?.tenant_id != null ? ` (tenant #${caller.tenant_id})` : ""}.
        </p>
      ) : null}

      <AppField label="Security groups (this company)">
        <div style={{ maxHeight: 140, overflow: "auto", display: "grid", gap: 4 }}>
          {groups.length === 0 ? (
            <span className="pg-muted" style={{ fontSize: 13 }}>
              {isPlatform && needsCompany && !tenantId
                ? "Select a company to load its security groups."
                : "No security groups for this company yet."}
            </span>
          ) : groups.map((g) => (
            <label key={g.id} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={groupIds.includes(g.id)}
                onChange={() => setGroupIds((ids) => ids.includes(g.id) ? ids.filter((x) => x !== g.id) : [...ids, g.id])}
                disabled={busy}
              />
              {groupLabel(g)}
            </label>
          ))}
        </div>
      </AppField>
      <div className="pg-modal-actions">
        <AppButton type="button" onClick={approve} disabled={busy}>{busy ? "Saving..." : "Approve & Grant Access"}</AppButton>
        <AppButton type="button" variant="secondary" onClick={reject} disabled={busy}>Reject</AppButton>
        <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</AppButton>
      </div>
    </AppModal>
  );
}

function ResetPasswordModal({
  user, open, onClose,
}: {
  user: User; open: boolean; onClose: () => void;
}) {
  const toast = useToast();
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setTemporaryPassword("");
    setGeneratedPassword(null);
    setErr("");
  }, [open, user.id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    setGeneratedPassword(null);
    try {
      const body = temporaryPassword.trim()
        ? { temporary_password: temporaryPassword.trim() }
        : {};
      const res = await api.post<{
        message: string;
        username: string;
        generated?: boolean;
        temporary_password?: string;
      }>(`/users/${user.id}/reset-password`, body);
      if (res.generated && res.temporary_password) {
        setGeneratedPassword(res.temporary_password);
        toast.success(`Password reset for ${res.username}`);
      } else {
        toast.success(`Password reset for ${res.username}`);
        onClose();
      }
    } catch (ex: unknown) {
      const msg = ex instanceof Error ? ex.message : "Failed to reset password";
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppModal open={open} onClose={onClose} title={`Reset password — ${user.username}`}>
      <form onSubmit={submit}>
        <p className="pg-muted" style={{ marginBottom: 12 }}>
          Set a new temporary password. Leave blank to generate one automatically.
          The user must change it after signing in.
        </p>
        <AppField label="Temporary password (optional)">
          <AppInput
            type="password"
            placeholder="Auto-generate if empty"
            value={temporaryPassword}
            onChange={(e) => setTemporaryPassword(e.target.value)}
            disabled={busy}
          />
        </AppField>
        {generatedPassword ? (
          <p className="pg-muted">
            Generated password (copy now — not shown again):{" "}
            <strong>{generatedPassword}</strong>
          </p>
        ) : null}
        {err && <p className="pg-error">{err}</p>}
        <div className="pg-modal-actions">
          <AppButton type="submit" disabled={busy}>
            {busy ? "Resetting…" : "Reset password"}
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy}>
            {generatedPassword ? "Close" : "Cancel"}
          </AppButton>
        </div>
      </form>
    </AppModal>
  );
}

export function UserManagement() {
  const toast = useToast();
  const currentUser = getUser();
  const canManage = hasPermission(currentUser, "user.manage");
  const canCreateField = hasPermission(currentUser, "user.create_field");
  const canCreate = canManage || canCreateField;
  const [users, setUsers] = useState<User[]>([]);
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [total, setTotal] = useState(0);
  const [pageState, setPageState] = useState<"loading"|"error"|"success">("loading");
  const [pageError, setPageError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [assignUser, setAssignUser] = useState<User | null>(null);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [approveUser, setApproveUser] = useState<PendingUser | null>(null);
  const [confirmToggle, setConfirmToggle] = useState<User | null>(null);
  const [resetUser, setResetUser] = useState<User | null>(null);
  const [actionBusy, setActionBusy] = useState<number | null>(null);

  const load = async () => {
    if (!canManage) {
      setUsers([]);
      setPendingUsers([]);
      setTotal(0);
      setPageState("success");
      return;
    }
    setPageState("loading");
    try {
      const d = await api.get<{ users: User[]; total: number }>("/users?per_page=200");
      const p = await api.get<{ pending_users: PendingUser[] }>("/users/pending-approvals");
      setUsers(d.users ?? []);
      setPendingUsers(p.pending_users ?? []);
      setTotal(d.total ?? d.users?.length ?? 0);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load users");
      setPageState("error");
    }
  };

  useEffect(() => { void load(); }, [canManage]);

  const toggleActive = async (user: User) => {
    setActionBusy(user.id);
    try {
      const action = user.is_active ? "deactivate" : "activate";
      await api.post(`/users/${user.id}/${action}`, {});
      toast.success(user.is_active ? `${user.username} deactivated` : `${user.username} activated`);
      await load();
    } catch (ex: unknown) {
      const msg = ex instanceof Error ? ex.message : "Failed to update user status";
      toast.error(msg);
      setPageError(msg);
    } finally { setActionBusy(null); }
  };

  const columns: ColumnDef<User, unknown>[] = [
    { accessorKey: "username", header: "Username" },
    {
      id: "name",
      header: "Name",
      accessorFn: (r) => [r.first_name, r.last_name].filter(Boolean).join(" ") || "—",
    },
    { accessorKey: "email", header: "Email" },
    {
      id: "scope",
      header: "Scope",
      cell: ({ row }) => {
        const scope = row.original.account_scope
          ?? (row.original.role === "system_admin" ? "platform" : "company");
        const label = row.original.account_scope_label
          ?? (scope === "platform" ? "Platform" : "Company");
        return (
          <AppBadge tone={scope === "platform" ? "warning" : "info"}>
            {label}
          </AppBadge>
        );
      },
    },
    { id: "role", header: "Role", cell: ({ row }) => <AppBadge tone="info">{ROLE_LABELS[row.original.role] ?? row.original.role}</AppBadge> },
    {
      id: "status", header: "Status",
      cell: ({ row }) =>
        row.original.is_active
          ? <AppBadge tone="success"><IconCheck size={12}/> Active</AppBadge>
          : <AppBadge tone="neutral">Inactive</AppBadge>,
    },
    { id: "created_at", header: "Created", accessorFn: (r) => new Date(r.created_at).toLocaleDateString("en-GB") },
    ...(canManage ? [{
      id: "actions", header: "Actions", enableSorting: false,
      cell: ({ row }) => (
        <div className="pg-row-actions">
          <AppButton
            type="button" variant="secondary"
            onClick={(e) => { e.stopPropagation(); setEditUser(row.original); }}
          >
            Edit
          </AppButton>
          <AppButton
            type="button" variant="secondary"
            onClick={(e) => { e.stopPropagation(); setAssignUser(row.original); }}
          >
            Groups
          </AppButton>
          <AppButton
            type="button" variant="secondary"
            onClick={(e) => { e.stopPropagation(); setResetUser(row.original); }}
          >
            Reset password
          </AppButton>
          <AppButton
            type="button" variant="secondary"
            disabled={actionBusy === row.original.id}
            onClick={(e) => { e.stopPropagation(); setConfirmToggle(row.original); }}
          >
            {actionBusy === row.original.id ? <IconSpinner size={12}/> : row.original.is_active ? "Deactivate" : "Activate"}
          </AppButton>
        </div>
      ),
    } as ColumnDef<User, unknown>] : []),
  ];

  const listStatus =
    pageState === "success" && users.length === 0 ? "empty" : pageState;

  return (
    <>
      {createOpen && <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} onSuccess={load} canManage={canManage} />}
      {canManage && editUser && (
        <EditUserModal
          user={editUser}
          open={!!editUser}
          onClose={() => setEditUser(null)}
          onSuccess={load}
        />
      )}
      {canManage && assignUser && (
        <AssignGroupsModal
          user={assignUser}
          open={!!assignUser}
          onClose={() => setAssignUser(null)}
          onSuccess={load}
        />
      )}
      {canManage && resetUser && (
        <ResetPasswordModal
          user={resetUser}
          open={!!resetUser}
          onClose={() => setResetUser(null)}
        />
      )}
      {canManage && approveUser && (
        <ApproveUserModal
          pendingUser={approveUser}
          open={!!approveUser}
          onClose={() => setApproveUser(null)}
          onSuccess={load}
        />
      )}

      <PageHeader
        title="Users"
        subtitle={
          canManage
            ? (currentUser?.role === "system_admin" || currentUser?.account_scope === "platform"
              ? "Platform ops and company Business Admins only. Company staff are managed by each BA."
              : "Manage accounts, roles, and access for your company.")
            : "Create field accounts within your delegated role limit."
        }
        actions={canCreate ? <AppButton type="button" onClick={() => setCreateOpen(true)}>Create User</AppButton> : undefined}
      />

      {!canCreate ? (
        <AppCard>
          <p className="pg-muted">You do not have permission to manage or create users.</p>
        </AppCard>
      ) : !canManage ? (
        <AppCard title="Field account creation">
          <p className="pg-muted">
            You can create Sales Rep, Technician, Village Chairman, or Customer accounts. Email is required.
            Existing users and administrator actions are available only to user managers.
          </p>
        </AppCard>
      ) : <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No users"
        emptyHint="Create a user to grant platform access."
      >
        <AppCard>
          <div className="pg-table-meta"><span>{total} user{total !== 1 ? "s" : ""}</span></div>
          <AppDataTable data={users} columns={columns} />
        </AppCard>
      </PageState>}

      {canManage && pageState === "success" && (
        <AppCard>
          <div className="pg-list-header">
            <h3>Pending Account Approvals</h3>
            <span className="pg-muted">{pendingUsers.length} pending</span>
          </div>
          {pendingUsers.length === 0 ? (
            <p className="pg-muted">No pending account requests.</p>
          ) : (
            <table className="pg-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Provider</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>
                    <td>{u.email}</td>
                    <td>{u.auth_provider ?? "password"}</td>
                    <td>{u.requested_at ? new Date(u.requested_at).toLocaleString("en-GB") : "—"}</td>
                    <td>
                      <AppButton type="button" variant="secondary" onClick={() => setApproveUser(u)}>
                        Review
                      </AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AppCard>
      )}

      {canManage ? <ConfirmAction
        open={!!confirmToggle}
        title={confirmToggle?.is_active ? "Deactivate user?" : "Activate user?"}
        message={
          confirmToggle
            ? confirmToggle.is_active
              ? `${confirmToggle.username} will lose access until reactivated.`
              : `Re-enable access for ${confirmToggle.username}?`
            : ""
        }
        confirmLabel={confirmToggle?.is_active ? "Deactivate" : "Activate"}
        danger={!!confirmToggle?.is_active}
        busy={actionBusy === confirmToggle?.id}
        onCancel={() => setConfirmToggle(null)}
        onConfirm={() => {
          const u = confirmToggle;
          setConfirmToggle(null);
          if (u) void toggleActive(u);
        }}
      /> : null}
    </>
  );
}
